import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import { Transition } from 'Shared/Transition';
import { process } from 'Utils/errorHandler';
import { buildFolderOptions } from 'Shared/flows/folders/folderUtils';

const CreateFolderModal = ({ open, onClose, folders, defaultParentId = null, editFolder = null, onSaved }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = !!editFolder;

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: { name: '', parent_folder_id: '' },
    });

    useEffect(() => {
        if (open) {
            reset({
                name: editFolder ? editFolder.name : '',
                parent_folder_id: editFolder ? '' : (defaultParentId ?? ''),
            });
        }
    }, [open, editFolder, defaultParentId, reset]);

    const parentOptions = buildFolderOptions(folders, editFolder ? editFolder.id : null);

    const onSubmit = (data) => {
        const url = isEdit ? `/folder/${editFolder.id}/update` : '/folder/create';
        const params = isEdit
            ? { name: data.name }
            : { name: data.name, parent_folder_id: data.parent_folder_id === '' ? null : data.parent_folder_id };

        dispatch(setLoading(true));
        http.post(url, params)
            .then((res) => {
                enqueueSnackbar(isEdit ? 'Folder renamed' : 'Folder created', { action: CustomSnackbar, variant: 'success' });
                onSaved(res.data.folder);
                onClose();
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-container': { alignItems: 'flex-start', paddingTop: '150px' },
                '& .MuiDialog-paper': { width: '50%', minWidth: '320px', maxWidth: 'none', margin: 0 },
            }}
        >
            <DialogTitle className="h1" variant="h1" sx={{ paddingBottom: '6px', textAlign: 'center', color: '#3B465C' }}>
                {isEdit ? 'Rename Folder' : 'Create Folder'}
            </DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        error={!!errors?.name}
                        helperText={errors?.name ? 'Folder name is required, at most 255 characters' : ''}
                        fullWidth
                        label="Folder name"
                        inputProps={{ maxLength: 255 }}
                        sx={{ marginTop: '10px' }}
                        {...register('name', { required: true, maxLength: 255 })}
                    />
                    {!isEdit && (
                        <TextField
                            select
                            fullWidth
                            label="Parent folder"
                            defaultValue={defaultParentId ?? ''}
                            sx={{ marginTop: '20px' }}
                            {...register('parent_folder_id')}
                        >
                            <MenuItem value="">None (top level)</MenuItem>
                            {parentOptions.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {`${'  '.repeat(opt.depth)}${opt.name}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined">Close</Button>
                <Button onClick={handleSubmit(onSubmit)} variant="contained">{isEdit ? 'Save' : 'Create'}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateFolderModal;
